import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMultiSelect } from '@cbhq/cds-common/select/useMultiSelect';
import { Button } from '@cbhq/cds-web/buttons';
import { TextInput } from '@cbhq/cds-web/controls';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import { isValidEmail } from '../../../utils/stringUtils';
import { EntityRoleDropdown } from '../components/EntityRoleDropdown';
import { LegalEntityDropdown } from '../components/LegalEntityDropdown';
import { useTeamApi } from '../useTeamApi';
import { canManageMembers } from '../utils/canManageMembers';
import type { MemberRole } from '../utils/constants';
import { ROUTE_TEAM } from '../utils/constants';

import { MultiLEInviteTray } from './MultiLEInviteTray';
import { SingleLEInviteTray } from './SingleLEInviteTray';

const messages = defineMessages({
  inviteMemberModalTitle: {
    id: 'Team.inviteMemberModalTitle',
    defaultMessage: 'Invite a new team member',
    description: 'Invite member modal title',
  },
  inviteMemberModalCloseAccessibilityLabel: {
    id: 'Team.inviteMemberModalCloseAccessibilityLabel',
    defaultMessage: 'Close',
    description: 'Accessibility label for the close button on the invite member modal',
  },
  inviteMemberEmailLabel: {
    id: 'Team.inviteMemberEmailLabel',
    defaultMessage: 'Send an invitation by email',
    description: 'Invite member email label',
  },
  inviteMemberEmailInvalidError: {
    id: 'Team.inviteMemberEmailInvalidError',
    defaultMessage: 'Invalid email address',
    description: 'Invite member email error',
  },
  inviteMemberEmailRequiredError: {
    id: 'Team.inviteMemberEmailRequiredError',
    defaultMessage: 'Email is required',
    description: 'Invite member email required error',
  },
  inviteMemberEmailPlaceholder: {
    id: 'Team.inviteMemberEmailPlaceholder',
    defaultMessage: 'Enter email address',
    description: 'Invite member email placeholder',
  },
  inviteMemberEmailErrorIconHelperText: {
    id: 'Team.inviteMemberEmailErrorIconHelperText',
    defaultMessage: 'Email error icon helper text',
    description: 'Invite member email error icon helper text',
  },
  inviteMemberRoleRequiredError: {
    id: 'Team.inviteMemberRoleRequiredError',
    defaultMessage: 'Role is required',
    description: 'Invite member role required error',
  },
  inviteMemberLegalEntityRequiredError: {
    id: 'Team.inviteMemberLegalEntityRequiredError',
    defaultMessage: 'At least one legal entity is required',
    description: 'Invite member legal entity required error',
  },
  inviteMemberModalButtonCancel: {
    id: 'Team.inviteMemberModalButtonCancel',
    defaultMessage: 'Cancel',
    description: 'Invite member modal button cancel',
  },
  inviteMemberModalButtonInvite: {
    id: 'Team.inviteMemberModalButtonInvite',
    defaultMessage: 'Send invite',
    description: 'Invite member modal button invite',
  },
  inviteMemberModalButtonInviteSuccess: {
    id: 'Team.inviteMemberModalButtonInviteSuccess',
    defaultMessage: 'Invite sent to {email}',
    description: 'Invite member modal button invite success',
  },
  inviteMemberModalButtonInviteError: {
    id: 'Team.inviteMemberModalButtonInviteError',
    defaultMessage: 'Error sending invite',
    description: 'Invite member modal button invite error',
  },
  unsupportedAccountError: {
    id: 'Team.unsupportedAccountError',
    defaultMessage:
      'This email already exists for a different account at coinbase, please provide a different email to invite into your organization.',
    description: 'Error message when user tries to invite a unsupported account',
  },
});

export function Invite() {
  const toast = useToast();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const isInviteTrayTreatment = useIsTreatment(AppExperiments.t0018_79dfabfe);

  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? '';

  // The workspace members API always returns the current user first.
  // canManageMembers only inspects members[0].roles (the current user).
  const orgMembersData = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );
  const canManage = useMemo(() => {
    const firstMember = orgMembersData?.members?.[0];
    const leRoles = (firstMember?.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ??
      []) as MemberRole[];
    const orgRoles = firstMember?.roles as string[] | undefined;
    return canManageMembers(
      [{ userUuid: '', firstName: '', lastName: '', email: '', roles: leRoles }],
      orgRoles,
    );
  }, [orgMembersData?.members]);

  useEffect(() => {
    if (orgMembersData?.members != null && !canManage) {
      navigate({ pathname: ROUTE_TEAM, search: location.search }, { replace: true });
    }
  }, [canManage, navigate, location.search, orgMembersData?.members]);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { value: roles, onChange: onRolesChange } = useMultiSelect<MemberRole>({
    initialValue: [],
  });

  const { value: selectedEntityIds, onChange: onEntityIdsChange } = useMultiSelect<string>({
    initialValue: [],
  });

  const { inviteMember } = useTeamApi(organizationId);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const emailInput = e.target.value;
      if (isValidEmail(emailInput) && emailError) {
        setEmailError(null);
      }
      setEmail(emailInput);
    },
    [emailError],
  );

  const handleRoleChange = useCallback(
    (newValues: MemberRole | MemberRole[] | null) => {
      setRoleError(null);
      onRolesChange(newValues);
    },
    [onRolesChange],
  );

  const handleEntityChange = useCallback(
    (newValues: string | string[] | null) => {
      setEntityError(null);
      onEntityIdsChange(newValues);
    },
    [onEntityIdsChange],
  );

  const handleClose = useCallback(() => {
    logEvent('member_invite_modal_closed_without_submit', {
      componentType: ComponentType.modal,
      action: ActionType.dismiss,
    });

    navigate({
      pathname: ROUTE_TEAM,
      search: location.search,
    });
  }, [navigate, location.search]);

  const handleInvite = useCallback(async () => {
    let emailValidationError: string | null = null;
    if (!email) {
      emailValidationError = formatMessage(messages.inviteMemberEmailRequiredError);
    } else if (!isValidEmail(email)) {
      emailValidationError = formatMessage(messages.inviteMemberEmailInvalidError);
    }
    const roleValidationError = !roles.length
      ? formatMessage(messages.inviteMemberRoleRequiredError)
      : null;
    const entityValidationError = !selectedEntityIds.length
      ? formatMessage(messages.inviteMemberLegalEntityRequiredError)
      : null;

    setEmailError(emailValidationError);
    setRoleError(roleValidationError);
    setEntityError(entityValidationError);

    if (
      emailValidationError !== null ||
      roleValidationError !== null ||
      entityValidationError !== null
    ) {
      logEvent('member_invite_validation_failed', {
        componentType: ComponentType.button,
        action: ActionType.click,
        validation_error_type: `email: ${emailValidationError ?? 'none'}, role: ${
          roleValidationError ?? 'none'
        }, entity: ${entityValidationError ?? 'none'}`,
      });
      return;
    }

    setIsLoading(true);

    try {
      markStep('submit_workspace_member_invite');
      logEvent('member_invite_submitted', {
        componentType: ComponentType.button,
        action: ActionType.click,
      });

      await inviteMember({ email, roles, legalEntityIds: selectedEntityIds });
      logEvent('member_invite_success', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      markStep('workspace_member_invite_success');
      toast.show(formatMessage(messages.inviteMemberModalButtonInviteSuccess, { email }));
      logEvent('member_modal_closed_after_save', {
        componentType: ComponentType.modal,
        action: ActionType.dismiss,
      });
      navigate({
        pathname: ROUTE_TEAM,
        search: location.search,
      });
    } catch (error) {
      let message: string | undefined;
      // TODO: Pull this from protos if possible
      if (error instanceof Error && error.message === 'UNSUPPORTED_ACCOUNT') {
        message = formatMessage(messages.unsupportedAccountError);
      }
      toast.show(message || formatMessage(messages.inviteMemberModalButtonInviteError), {
        variant: 'bgNegative',
      });
      logEvent('member_invite_failed', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    email,
    roles,
    selectedEntityIds,
    formatMessage,
    inviteMember,
    toast,
    navigate,
    location.search,
  ]);

  useEffect(() => {
    markStep('loaded_workspace_member_invite_modal');
    logEvent('member_invite_modal_opened', {
      componentType: ComponentType.modal,
      action: ActionType.view,
    });
  }, []);

  const legalEntityCount = (selectedOrganization?.legalEntities ?? []).length;
  const isMultiLE = legalEntityCount > 1;
  const shouldForceInviteTrayLocally = process.env.NODE_ENV === 'development';
  const shouldRenderInviteTray = shouldForceInviteTrayLocally || isInviteTrayTreatment;

  if (shouldRenderInviteTray) {
    return isMultiLE ? (
      <MultiLEInviteTray onClose={handleClose} />
    ) : (
      <SingleLEInviteTray onClose={handleClose} />
    );
  }

  return (
    <Modal onRequestClose={handleClose} visible width={480}>
      <ModalHeader
        closeAccessibilityLabel={formatMessage(messages.inviteMemberModalCloseAccessibilityLabel)}
        testID="invite-member-modal"
        title={formatMessage(messages.inviteMemberModalTitle)}
      />
      <ModalBody alignItems="center">
        <VStack gap={4} paddingTop={2} width="100%">
          <TextInput
            type="email"
            value={email}
            onChange={handleEmailChange}
            label={formatMessage(messages.inviteMemberEmailLabel)}
            placeholder={formatMessage(messages.inviteMemberEmailPlaceholder)}
            accessibilityLabel={formatMessage(messages.inviteMemberEmailLabel)}
            helperTextErrorIconAccessibilityLabel={formatMessage(
              messages.inviteMemberEmailErrorIconHelperText,
            )}
            variant={emailError ? 'negative' : undefined}
            helperText={emailError}
            required
          />
          <EntityRoleDropdown
            value={roles}
            onChange={handleRoleChange}
            variant={roleError ? 'negative' : undefined}
            helperText={roleError}
          />
          <LegalEntityDropdown
            value={selectedEntityIds}
            onChange={handleEntityChange}
            variant={entityError ? 'negative' : undefined}
            helperText={entityError}
          />
        </VStack>
      </ModalBody>
      <ModalFooter
        primaryAction={
          <Button onClick={handleInvite} disabled={isLoading} loading={isLoading}>
            {formatMessage(messages.inviteMemberModalButtonInvite)}
          </Button>
        }
        secondaryAction={
          <Button onClick={handleClose} variant="secondary" disabled={isLoading}>
            {formatMessage(messages.inviteMemberModalButtonCancel)}
          </Button>
        }
      />
    </Modal>
  );
}
